import { useEffect, useState } from 'react';
import {
  api,
  type LastStageConfig,
  type PlannedStageInfo,
  type RangeSession,
  type DrillRow,
} from '../lib/api';
import { errorText } from '../lib/errorText';
import { formatMagazines } from '../lib/magazines';
import { useUncalibrated } from '../lib/calibration';
import { UncalibratedBadge } from '../components/UncalibratedBadge';

interface Props {
  session: RangeSession;
  /**
   * Active when the referee tapped "Ersetzen" on a planned stage. The
   * next library pick replaces the slot's drill instead of creating
   * a new ad-hoc stage. The picker still routes through mode / distance
   * pickers afterwards so the referee can adjust on the fly.
   */
  replaceForStageId?: string | undefined;
  /**
   * Called after the referee picks a drill from the library. The
   * parent then navigates to the mode picker.
   */
  onPicked: (drill: DrillRow) => void;
  /**
   * Called after the referee taps "Starten" on the pinned planned stage.
   * Passes the whole stage so the parent can confirm/seed its discipline
   * before activating it.
   */
  onStartPlanned: (stage: PlannedStageInfo['stage']) => void;
  /**
   * Called after the referee confirmed "Überspringen" on the pinned planned
   * stage. Awaited; the picker then reloads the next planned stage itself.
   */
  onSkipPlanned: (stageId: string) => void | Promise<void>;
  /** Called when the referee taps "Ersetzen" — parent flips into replace mode. */
  onReplacePlanned: (stageId: string) => void;
  /** Build a custom one-off drill from scratch (target/shots/mode/distance/PAR). */
  onCustom: () => void;
  /** Repeat the previous stage — opens the custom builder pre-filled. */
  onRepeatPrevious: (cfg: LastStageConfig) => void;
  onCancel: () => void;
}

function fmtDistance(cm: number | null): string {
  if (cm == null) return '— m';
  const m = cm / 100;
  return `${m.toLocaleString('de-AT', { maximumFractionDigits: 2 })} m`;
}

/**
 * Step 1 of the round-setup flow: picks a drill for the next round.
 *
 * If a planned stage is queued for this session, it shows pinned at
 * the top with three actions: Start (activates the slot), Skip (marks
 * skipped, advances), Replace (parent flips into replace mode and the
 * next library pick rewrites the slot). The library lists below; ad-
 * hoc free picks still work and never auto-consume the planned slot.
 */
export function DrillPicker({
  session,
  replaceForStageId,
  onPicked,
  onStartPlanned,
  onSkipPlanned,
  onReplacePlanned,
  onCustom,
  onRepeatPrevious,
  onCancel,
}: Props) {
  const [drills, setDrills] = useState<DrillRow[] | null>(null);
  const [planned, setPlanned] = useState<PlannedStageInfo | null>(null);
  const [lastStage, setLastStage] = useState<LastStageConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'start' | 'skip' | null>(null);
  // Bumped by "Erneut versuchen" to re-run the loads below.
  const [attempt, setAttempt] = useState(0);
  const isUncalibrated = useUncalibrated(session.locationId);

  useEffect(() => {
    setError(null);
    void api
      .drills(session.locationId ?? null)
      .then(setDrills)
      .catch((err) => setError(errorText(err)));
    void api
      .nextPlannedStage(session.id)
      .then(setPlanned)
      .catch((err) => console.warn('nextPlannedStage failed', err));
    void api
      .lastStageConfig(session.id)
      .then(setLastStage)
      .catch((err) => console.warn('lastStageConfig failed', err));
  }, [session.id, session.locationId, attempt]);

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <button onClick={onCancel} className="min-h-12 pr-3 text-sm text-zinc-500 underline">
          ← zurück
        </button>
        <h2 className="text-lg font-bold">
          {replaceForStageId ? 'Slot ersetzen' : 'Übung starten'}
        </h2>
      </header>

      {replaceForStageId && (
        <div className="rounded border border-amber-700 bg-amber-900/30 px-4 py-3 text-sm text-amber-200">
          Wähle eine Übung aus der Bibliothek — der geplante Slot wird damit ersetzt (Reihenfolge
          bleibt gleich).
        </div>
      )}

      {error && (
        <div role="alert" className="space-y-3 rounded bg-red-900/30 p-3 text-sm text-red-300">
          <p>Fehler: {error}</p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100 hover:bg-red-900/40"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {!replaceForStageId && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onCustom}
            className="min-h-12 flex-1 rounded-lg border border-emerald-600 bg-emerald-900/20 px-4 py-3 text-left font-semibold text-emerald-100 transition-colors hover:border-emerald-400 hover:bg-emerald-900/40"
          >
            ＋ Eigene Übung erstellen
            <span className="block text-xs font-normal text-emerald-200/70">
              Scheibe, Schüsse, Wertung, Distanz & PAR frei wählen.
            </span>
          </button>
          {lastStage && (
            <button
              type="button"
              onClick={() => onRepeatPrevious(lastStage)}
              className="min-h-12 flex-1 rounded-lg border border-zinc-600 bg-zinc-900 px-4 py-3 text-left font-semibold text-zinc-200 transition-colors hover:border-zinc-400"
            >
              ↻ Vorheriges wiederholen
              <span className="block text-xs font-normal text-zinc-400">{lastStage.name}</span>
            </button>
          )}
        </div>
      )}

      {!replaceForStageId && planned && (
        <div className="rounded-lg border border-emerald-600 bg-emerald-900/20 p-4">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <span className="text-base font-semibold">
              Geplant: Stage {planned.stage.ord} · {planned.stage.name}
            </span>
            <span className="rounded-full bg-emerald-800 px-2 py-0.5 text-xs text-emerald-100">
              {planned.stage.scoringMode === 'speed' ? 'Speed' : 'Präzision'}
            </span>
          </div>
          <div className="text-xs text-emerald-200/80">
            {fmtDistance(planned.stage.distanceCm)} · {planned.stage.shotCount} Schuss
            {planned.stage.timeLimitS != null ? ` · ${planned.stage.timeLimitS} s` : ''}
          </div>
          {planned.drillDescription && (
            <div className="mt-1 text-xs text-emerald-200/60">{planned.drillDescription}</div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => {
                setBusy('start');
                onStartPlanned(planned.stage);
              }}
              disabled={busy !== null}
              className="min-h-12 rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
            >
              {busy === 'start' ? 'Starte…' : 'Starten'}
            </button>
            <button
              onClick={async () => {
                if (!confirm('Stage überspringen?')) return;
                setBusy('skip');
                try {
                  await onSkipPlanned(planned.stage.id);
                  // Same view, no remount: load the next planned stage
                  // (if any) into the pinned card ourselves.
                  setPlanned(await api.nextPlannedStage(session.id));
                } catch (err) {
                  console.warn('nextPlannedStage failed', err);
                } finally {
                  setBusy(null);
                }
              }}
              disabled={busy !== null}
              className="min-h-12 rounded border border-zinc-600 px-4 py-2 text-sm text-zinc-200 hover:border-zinc-400 disabled:opacity-60"
            >
              {busy === 'skip' ? 'Überspringe…' : 'Überspringen'}
            </button>
            <button
              onClick={() => onReplacePlanned(planned.stage.id)}
              disabled={busy !== null}
              className="min-h-12 rounded border border-zinc-600 px-4 py-2 text-sm text-zinc-200 hover:border-zinc-400 disabled:opacity-60"
            >
              Ersetzen
            </button>
          </div>
        </div>
      )}

      {drills == null ? (
        <p className="text-sm text-zinc-500">Lade Übungen…</p>
      ) : drills.length === 0 ? (
        <div className="rounded border border-dashed border-zinc-700 p-4 text-sm text-zinc-400">
          Keine Übungen hinterlegt. Im Admin-Bereich „Übungen“ anlegen.
        </div>
      ) : (
        <ul className="space-y-2">
          {drills.map((s) => (
            <li key={s.drill.id}>
              <button
                onClick={() => onPicked(s)}
                className="flex min-h-12 w-full items-stretch gap-3 rounded-lg border border-zinc-700 px-4 py-3 text-left transition-colors hover:border-emerald-500"
              >
                {s.targetImageBlobId ? (
                  <img
                    src={`/api/blobs/${s.targetImageBlobId}`}
                    alt={s.targetName}
                    className="h-16 w-16 shrink-0 self-center rounded bg-zinc-800 object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-16 w-16 shrink-0 self-center rounded border border-dashed border-zinc-700" />
                )}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-base font-semibold">{s.drill.name}</span>
                    <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300">
                      {s.schemaName}
                    </span>
                  </div>
                  {isUncalibrated(s.drill.targetTypeId) && (
                    <div>
                      <UncalibratedBadge />
                    </div>
                  )}
                  <div className="text-xs text-zinc-400">
                    {fmtDistance(s.drill.distanceCm)} · {s.drill.shotCount} Schuss
                    {formatMagazines(s.drill.magazines)
                      ? ` (${formatMagazines(s.drill.magazines)})`
                      : ''}
                    {s.drill.timeLimitS != null ? ` · ${s.drill.timeLimitS} s` : ''} ·{' '}
                    {s.targetName}
                  </div>
                  {s.drill.description && (
                    <div className="text-xs text-zinc-500">{s.drill.description}</div>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
